// Escaneo continuo de bultos para cargar al camión (agencia / camión / gira).
// El encargado escanea TODOS los bultos seguidos sin entrar a cada pedido:
//  - la app detecta sola a qué pedido pertenece cada QR (formato "pedidoId|n/total")
//  - suena un beep corto por cada bulto escaneado
//  - suena un "ding" distinto cuando se completa un pedido → se marca CARGADO
//  - muestra el progreso en vivo de todos los pedidos

import React, { useRef, useState, useCallback, useMemo } from "react";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase";
import QrScanner from "../../../components/QrScanner";
import { useAuth, hasRol } from "../../../context/AuthContext";

// ── Sonidos (Web Audio, sin archivos) ──────────────────────────
let _audioCtx = null;
function ctx() {
  try {
    if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    return _audioCtx;
  } catch { return null; }
}
function tono(freqs, dur = 0.12, vol = 0.2, type = "sine") {
  const c = ctx(); if (!c) return;
  const o = c.createOscillator(); const g = c.createGain();
  o.connect(g); g.connect(c.destination); o.type = type;
  freqs.forEach((f, i) => o.frequency.setValueAtTime(f, c.currentTime + i * (dur / freqs.length)));
  g.gain.setValueAtTime(vol, c.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + dur);
  o.start(); o.stop(c.currentTime + dur);
}
const playBulto    = () => tono([1100], 0.07, 0.18);                 // beep corto
const playCompleto = () => tono([880, 1318.5], 0.28, 0.2);           // ding ascendente
const playError    = () => tono([300, 200], 0.22, 0.22, "square");   // buzz grave

export default function EscaneoContinuoModal({ pedidos, collection: col = "pedidos_despachados", onClose }) {
  // pedidos: [{ id, numero, cliente, bultos, agencia? }]
  const { profile } = useAuth();
  const esAdmin = hasRol(profile, "admin");
  const pedidosMap = useMemo(() => Object.fromEntries(pedidos.map(p => [p.id, p])), [pedidos]);

  // refs = estado siempre actual (el scanner dispara muy seguido)
  const scannedRef    = useRef({});          // { pedidoId: Set<bultoN> }
  const completadosRef = useRef(new Set());  // pedidoIds ya marcados CARGADO

  const [, force] = useState(0);
  const rerender = () => force(n => n + 1);
  const [ultimo, setUltimo] = useState(null); // feedback del último escaneo
  const [error, setError]   = useState("");
  const [modoManual, setModoManual] = useState(false); // etiqueta rota / sin escáner → marcar a mano

  async function marcarCargado(pid, bultos) {
    try {
      await updateDoc(doc(db, col, pid), {
        cargaEstado: "CARGADO",
        cargadoAt: serverTimestamp(),
        bultosEscaneados: [...bultos].sort((a, b) => a - b),
      });
    } catch (e) {
      console.error("[EscaneoContinuo] no se pudo marcar CARGADO", pid, e);
      setError("Error al guardar la carga de #" + pid);
    }
  }

  const handleScan = useCallback((raw) => {
    const texto = typeof raw === "string" ? raw : raw?.text || raw?.decodedText || String(raw);
    const m = texto.match(/^(.+)\|(\d+)\/(\d+)$/);
    if (!m) return; // en modo continuo ignoramos QR que no son de bulto
    const pid = m[1];
    const bultoN = Number(m[2]);

    const pedido = pedidosMap[pid];
    if (!pedido) { setError(`#${pid} no pertenece a esta carga`); playError(); rerender(); return; }

    // Candado: sin factura no se puede cargar (salvo admin).
    if (pedido.facturado !== true && !esAdmin) {
      setError(`#${pedido.numero || pid} sin facturar — no se puede cargar`);
      playError(); rerender(); return;
    }

    if (!scannedRef.current[pid]) scannedRef.current[pid] = new Set();
    const set = scannedRef.current[pid];
    if (set.has(bultoN)) return; // ya escaneado → ignorar (sin re-beep)

    set.add(bultoN);
    setError("");
    const total = Math.max(1, Number(pedido.bultos) || 1);

    if (set.size >= total && !completadosRef.current.has(pid)) {
      completadosRef.current.add(pid);
      marcarCargado(pid, set);
      playCompleto();
      setUltimo({ pid, numero: pedido.numero || pid, completo: true, size: set.size, total });
    } else {
      playBulto();
      setUltimo({ pid, numero: pedido.numero || pid, completo: false, size: set.size, total });
    }
    rerender();
  }, [pedidosMap, esAdmin]);

  // Marcar/desmarcar TODO un pedido a mano (etiqueta rota o sin escáner). A diferencia
  // del escaneo, acá escribimos CARGADO en Firestore al instante (no hay batch final).
  async function toggleManualCarga(pid) {
    const pedido = pedidosMap[pid];
    if (!pedido) return;
    if (pedido.facturado !== true && !esAdmin) {
      setError(`#${pedido.numero || pid} sin facturar — no se puede cargar`);
      playError(); rerender(); return;
    }
    const total = Math.max(1, Number(pedido.bultos) || 1);
    if (completadosRef.current.has(pid)) {
      // Estaba cargado → lo revertimos a "sin cargar".
      completadosRef.current.delete(pid);
      scannedRef.current[pid] = new Set();
      setError("");
      rerender();
      try {
        await updateDoc(doc(db, col, pid), { cargaEstado: null, cargadoAt: null, bultosEscaneados: [] });
      } catch (e) {
        console.error("[EscaneoContinuo] no se pudo quitar la carga", pid, e);
        setError("No se pudo quitar la carga de #" + (pedido.numero || pid));
      }
    } else {
      // Marcar todos los bultos y CARGADO.
      const all = new Set();
      for (let i = 1; i <= total; i++) all.add(i);
      scannedRef.current[pid] = all;
      completadosRef.current.add(pid);
      setError("");
      playCompleto();
      setUltimo({ pid, numero: pedido.numero || pid, completo: true, size: total, total });
      rerender();
      await marcarCargado(pid, all);
    }
  }

  // Ordenar para la lista: incompletos primero, después completados. Se calcula INLINE
  // en cada render (lee los refs actuales) para que la marca manual se refleje al toque.
  const lista = [...pedidos].sort((a, b) => {
    const ca = completadosRef.current.has(a.id) ? 1 : 0;
    const cb = completadosRef.current.has(b.id) ? 1 : 0;
    return ca - cb;
  });

  const totalPedidos    = pedidos.length;
  const totalCompletos  = completadosRef.current.size;

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0f172a", zIndex: 1000, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #1e293b" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 800, color: "#fff" }}>📷 Escaneo continuo</h3>
          <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#94a3b8" }}>
            {totalCompletos}/{totalPedidos} pedidos cargados
          </p>
        </div>
        <button onClick={onClose} style={{ background: "#1e293b", border: "none", color: "#fff", borderRadius: "10px", padding: "8px 14px", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}>
          Terminar
        </button>
      </div>

      {/* Cámara + toggle manual */}
      <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", alignItems: "center" }}>
        {!modoManual && <QrScanner onScanSuccess={handleScan} qrbox={{ width: 240, height: 240 }} />}
        {/* Feedback último escaneo */}
        <div style={{ marginTop: "8px", minHeight: "24px", textAlign: "center" }}>
          {error ? (
            <span style={{ color: "#fca5a5", fontSize: "13px", fontWeight: 700 }}>⚠️ {error}</span>
          ) : ultimo ? (
            <span style={{ color: ultimo.completo ? "#86efac" : "#bae6fd", fontSize: "13px", fontWeight: 700 }}>
              {ultimo.completo ? `✅ #${ultimo.numero} completo (${ultimo.total} bultos)` : `📦 #${ultimo.numero} — ${ultimo.size}/${ultimo.total}`}
            </span>
          ) : (
            <span style={{ color: "#64748b", fontSize: "13px" }}>{modoManual ? "Tocá cada pedido para marcarlo cargado" : "Apuntá a las etiquetas, una tras otra…"}</span>
          )}
        </div>
        <button onClick={() => setModoManual(m => !m)}
          style={{ marginTop: "6px", background: "none", border: "1px solid #334155", color: "#94a3b8", borderRadius: "8px", padding: "5px 12px", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}>
          {modoManual ? "📷 Volver a escanear" : "✋ Marcar a mano (etiqueta rota)"}
        </button>
      </div>

      {/* Lista de pedidos con progreso */}
      <div style={{ flex: 1, overflowY: "auto", padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: "6px" }}>
        {lista.map(p => {
          const set = scannedRef.current[p.id];
          const size = set ? set.size : 0;
          const total = Math.max(1, Number(p.bultos) || 1);
          const completo = completadosRef.current.has(p.id);
          return (
            <div key={p.id} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              background: completo ? "#14532d" : size > 0 ? "#1e3a5f" : "#1e293b",
              border: `1px solid ${completo ? "#16a34a" : size > 0 ? "#2563eb" : "#334155"}`,
              borderRadius: "10px", padding: "10px 14px",
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: "13px", fontWeight: 700, color: "#fff" }}>
                  {completo ? "✅ " : ""}{p.numero || p.id}
                  {p.agencia ? <span style={{ color: "#93c5fd", fontWeight: 600 }}> · {p.agencia}</span> : ""}
                </div>
                <div style={{ fontSize: "12px", color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.cliente}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                <span style={{ fontSize: "13px", fontWeight: 800, color: completo ? "#86efac" : "#bae6fd" }}>
                  {size}/{total}
                </span>
                {modoManual && (
                  <button onClick={() => toggleManualCarga(p.id)}
                    style={{ background: completo ? "#334155" : "#2563eb", border: "none", color: "#fff", borderRadius: "8px", padding: "5px 10px", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}>
                    {completo ? "Quitar" : "Cargar"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
